import { useState, useEffect, useCallback, useMemo } from 'react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { listOrgMembers } from '../../services/api';
import {
  getAgencyClinics,
  type AgencyClinicResponse,
} from '../../services/clinicsApi';
import {
  listRogerDocAccountLinks,
  buildRogerDocLinksByEmail,
  type RogerDocClinicianAccount,
} from '../../services/accountLinkApi';
import type { OrgMemberResponse } from './types';

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useClinicianManager() {
  const { user, hasAnyPermission, showError } = useStore(
    useShallow(state => ({
      user: state.user,
      hasAnyPermission: state.hasAnyPermission,
      showError: state.showError,
    }))
  );

  // ── Data state ──
  const [members, setMembers] = useState<OrgMemberResponse[]>([]);
  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // ── RogerDoc account links (fetched separately so the member list renders
  //    even if RogerDoc is slow/unavailable; RogerDoc is the source of truth) ──
  const [rogerDocAccounts, setRogerDocAccounts] = useState<
    RogerDocClinicianAccount[]
  >([]);

  // ── Invite dialog state ──
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // ── Edit dialog state (RBAC dialog reads member + clinics from here) ──
  const [editingMember, setEditingMember] = useState<OrgMemberResponse | null>(
    null
  );

  // ── Data loading ──

  const loadData = useCallback(async () => {
    if (!user?.agency?.id) return;
    setIsLoading(true);
    try {
      const [membersRes, clinicsData] = await Promise.all([
        listOrgMembers({}),
        getAgencyClinics(user.agency.id),
      ]);
      setMembers(membersRes.data ?? []);
      setClinics(clinicsData);
    } catch (err) {
      console.error('Failed to load clinician manager data:', err);
      showError('Failed to load organization members');
    } finally {
      setIsLoading(false);
    }
  }, [user?.agency?.id, showError]);

  const loadRogerDocLinks = useCallback(async () => {
    if (!user?.agency?.id) return;
    try {
      const accounts = await listRogerDocAccountLinks();
      setRogerDocAccounts(accounts);
    } catch (err) {
      // Non-fatal: the member list stays usable without link info.
      console.error('Failed to load RogerDoc account links:', err);
    }
  }, [user?.agency?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Separate, non-blocking fetch (per ticket: a separate call after FE loads).
  useEffect(() => {
    loadRogerDocLinks();
  }, [loadRogerDocLinks]);

  const rogerDocLinksByEmail = useMemo(
    () => buildRogerDocLinksByEmail(rogerDocAccounts, user?.agency?.id),
    [rogerDocAccounts, user?.agency?.id]
  );

  // EMR-375: every clinic is Roger-assignable (including Kinnser-participating).
  const roleAssignableClinics = clinics;

  // No longer hide formerly KINNSER_SYNCED rows from role assignment.
  const kinnserClinicIds = useMemo(() => new Set<string>(), []);

  // ── Filtered members (memoized) ──

  const filteredMembers = useMemo(() => {
    if (!searchQuery) return members;
    const q = searchQuery.toLowerCase();
    return members.filter(
      m =>
        (m.first_name ?? '').toLowerCase().includes(q) ||
        (m.last_name ?? '').toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q)
    );
  }, [members, searchQuery]);

  // ── Edit dialog actions ──

  const openEditDialog = useCallback((member: OrgMemberResponse) => {
    setEditingMember(member);
  }, []);

  const closeEditDialog = useCallback(() => {
    setEditingMember(null);
  }, []);

  // ── Permission check ──

  const hasAccess = hasAnyPermission(['clinician-mgr:access', 'emr:manage']);

  return {
    // Permission
    hasAccess,

    // Data
    members,
    clinics,
    roleAssignableClinics,
    kinnserClinicIds,
    filteredMembers,
    isLoading,
    searchQuery,
    setSearchQuery,
    loadData,

    // RogerDoc account links
    rogerDocAccounts,
    rogerDocLinksByEmail,
    loadRogerDocLinks,

    // Invite dialog
    showInviteDialog,
    openInviteDialog: () => setShowInviteDialog(true),
    closeInviteDialog: () => setShowInviteDialog(false),

    // Edit dialog (RoleAssignmentDialog uses bulk RBAC APIs internally)
    editingMember,
    openEditDialog,
    closeEditDialog,
  };
}
